// Third-party
import get from 'lodash/get';
import round from 'lodash/round';
import moment from 'moment';

// Constants
import { DEFAULT_GROWTH_VALUES, PERIOD_YEARS, MAX_CALENDAR_RETURNS, TIME_PERIODS, DEFAULT_RISK_VALUES, Interval, COMPARISON_ITEM_TYPES } from './constants';
import { HoldingAllocationMode } from '@/enums/Portfolio';

// Types
import Dictionary from '@/types/dictionary';
import UpDownMonths from '@/types/up-down-months';
import RiskPeriods from '@/types/risk-periods';
import TimePeriods from '@/types/time-periods';
import PortfolioHolding from '@/types/portfolio-holding';
import DataItem from '@/types/data-item';
import Portfolio from '@/types/portfolio';
import Holding from '@/types/holding';
import PortfolioRisk from '@/types/portfolio-risk';
import Risk from '@/types/risk';
import RollingPerformance from '@/types/rolling-performance';
import SortDirection from '@/types/sort-direction';
import RollingIntervals from '@/types/rolling-intervals';
import InvestmentItem from '@/types/investment-item';
import Nullable from '@/types/nullable';
import PercentGrowth from '@/types/percent-growth';
import CalendarYearReturns from '@/types/calendar-year-returns';
import Timestamp from '@/types/timestamp';
import BestWorst from '@/types/best-worst';
import BestWorstPerformance from '@/types/best-worst-performance';
import PriceData from '@/types/price-data';
import PortfolioHoldingType from '@/types/portfolio-holding-type';
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import CorrelationDataInput from '@/types/correlation-data-input';
import CorrelationData from '@/types/correlation-data';
import GrowthTypes from '@/types/growth-types';
import AllocationDetails from '@/types/allocation-details';

import Calculator from '@capintel/instruments-lib';

// Utils
import { getLastNYears, periodToMonths, periodToYear, getLastDayOfPreviousMonth, MAX_TIMESTAMP, MIN_TIMESTAMP } from './time';
import { HOLDING_TYPES, PortfolioData, getPortfolioHoldingData } from './portfolios';
import { sum, product } from './math';
import CommonInception from '@/types/common-inception';
import ComparisonHolding from '@/types/comparison-holding';
import { getMaxCommonInceptionDate } from './common-inception';

/**
 * Filter out portfolio holdings based on id
 * @param {PortfolioHolding[]} portfolioHoldings Portfolio holdings list
 * @param {string[]} ids array of ids to filter out
 * @returns {PortfolioHolding[]} filtered Portfolio holdings
 */
export const filterPortfolioHoldings = (portfolioHoldings: PortfolioHolding[], ids: string[]) =>
  portfolioHoldings.filter(holding => !ids.some(id => id === holding.original.id));

export const holdingIdsEmptyAtPath = (holdings: DataItem[], path: string): string[] =>
  holdings.reduce((ids: string[], h) => {
    const val = get(h, path, null);
    if (val == null) {
      ids.push(h._id);
    }
    return ids;
  }, []);

/**
 * Get list of periods that all funds have in common for a specific performance metric
 * @param {Portfolio} portfolio Portfolio to be calculated
 * @param {Holding[]} data key value store for Funds and Stocks
 * @returns {string[]} array of common periods
 */
export const getCommonPeriods = (portfolio: Portfolio, data: Holding[], type: string): string[] => {
  const referenceList = [...Object.keys(TIME_PERIODS)].filter(p => p !== TIME_PERIODS.INC && p !== TIME_PERIODS.CI);
  const holdings = data.filter(d => portfolio.holdings.some(holding => holding.original.id === d._id));

  return referenceList.filter(period => holdings.every(h => get(h, `performance.${type}.${period}`) != null));
};

/**
 * Get list of years that all holdings have in common (use calendarYear)
 * @param {Portfolio} portfolio Portfolio to be calculated
 * @param {Holding[]} data key value store for Funds and Stocks
 * @returns {Array<number>} array of common years
 */
export const getCommonYears = (portfolio: Portfolio, data: Holding[]): Array<number> => {
  const years = getLastNYears(MAX_CALENDAR_RETURNS);
  const holdings = data.filter(d => portfolio.holdings.some(holding => holding.original.id === d._id));
  return years.filter(year => holdings.every(h => get(h, `performance.calendarYear.${year}`) != null));
};

/**
 * Calculates the total portfolio size with new set of holdings
 * @param {PortfolioHolding[]} portfolioHoldings List of portfolio holdings.
 * @returns {number} New portfolio size.
 */
export const sumDollars = (portfolioHoldings: PortfolioHolding[]) => portfolioHoldings.reduce((total, holding) => total + holding.dollars, 0);

/**
 * Reallocates the holdings weight/dollars given portfolio size and new holdings
 * @param {number} size Portfolio.size
 * @param {PortfolioHolding[]} holdings Portfolio holdings
 * @returns {PortfolioHolding[]} Holdings with adjusted weightings
 */
export const reallocate = (size: number, holdings: PortfolioHolding[]) => {
  const total = sumDollars(holdings);
  return holdings.map(holding => {
    const weight = total !== 0 ? round((holding.dollars / total) * 100, 2) : 0;
    return {
      ...holding,
      weight,
      dollars: round((size * weight) / 100, 2),
    };
  });
};

export const initPercentGrowth = (): PercentGrowth =>
  Object.keys(TIME_PERIODS).reduce((acc, key) => {
    acc[key as GrowthTypes] = null;
    return acc;
  }, {} as PercentGrowth);

/**
 * Returns the initial risk object for a portfolio or Fund
 */
export const initRiskData = (isPortfolio?: boolean): PortfolioRisk | Risk => {
  const risk: Risk = {
    rSquared: { ...DEFAULT_RISK_VALUES },
    sharpeRatio: { ...DEFAULT_RISK_VALUES },
    sortino: { ...DEFAULT_RISK_VALUES },
    beta: { ...DEFAULT_RISK_VALUES },
    maximumDrawdown: { ...DEFAULT_RISK_VALUES },
    upside: { ...DEFAULT_RISK_VALUES },
    standardDeviation: { ...DEFAULT_RISK_VALUES },
    downside: { ...DEFAULT_RISK_VALUES },
    alpha: { ...DEFAULT_RISK_VALUES },
    infoRatio: { ...DEFAULT_RISK_VALUES },
    trackingError: { ...DEFAULT_RISK_VALUES },
    correlationCoefficient: { ...DEFAULT_RISK_VALUES },
  };
  return risk;
};

/**
 * Get the common timestamp for an array of rolling performances
 * @param rollings an array of rolling performances
 * @param {SortDirection} sortDirection the sorting direction **ASC** OR **DESC**
 * **ASC** means the earliest common timestamp, **DESC** is latest common timestamp
 * @param {Date} startDate optional start date as the starting point
 * @returns {{ monthly: number, weekly: number }} returns a dictionary of the weekly and monthly common timestamp
 */
export const getCommonTimestamps = (rollings: RollingPerformance[], sortDirection: SortDirection, startDate?: Date): { monthly: number; weekly: number } => {
  const isAscending = sortDirection === 'ASC';
  const initialVal = {
    weekly: isAscending ? MIN_TIMESTAMP : MAX_TIMESTAMP,
    monthly: isAscending ? MIN_TIMESTAMP : MAX_TIMESTAMP,
  };
  if (startDate) {
    initialVal.weekly = startDate.getTime();
    initialVal.monthly = startDate.getTime();
  }
  return rollings.reduce((dates: { monthly: number; weekly: number }, rolling) => {
    for (const period of [Interval.Weekly, Interval.Monthly] as RollingIntervals[]) {
      if (rolling[period] != null && rolling[period].length > 0) {
        const rollingStart = sortDirection === 'ASC' ? rolling[period][0][0] : rolling[period][rolling[period].length - 1][0];
        const prevDate = dates[period];
        if (isAscending && rollingStart > prevDate) {
          dates[period] = rollingStart;
        } else if (!isAscending && rollingStart < prevDate) {
          dates[period] = rollingStart;
        }
      }
    }
    return dates;
  }, initialVal);
};

/**
 * Calculate weighted average of fund values for property path
 * @param {Portfolio} portfolio Portfolio to be calculated
 * @param {DataItem[]} data Data for holdings or equity items
 * @param {string} path the path of the property to calculate
 * @return {number} weighted average value
 */
export const weightedAverage = (portfolio: Portfolio, data: DataItem[], path: string, allowReallocate: boolean = true): number | undefined => {
  const ids = holdingIdsEmptyAtPath(data, path);
  let { holdings } = portfolio;

  if (allowReallocate && portfolio.size && ids.length > 0) {
    holdings = reallocate(portfolio.size, filterPortfolioHoldings(portfolio.holdings, ids));
  }
  return weightedAverageForHoldings(holdings, data, path);
};

/**
 * Calculates the weighted average rating from a list of allocations based on the given ratings.
 * @param {T} ratings - An enum-like object where keys are rating names and values are numerical scores.
 * @param {AllocationDetails[]} [allocations] - An optional array of allocation details containing rating names and weights.
 * @returns The name of the rating that corresponds to the weighted average score, or undefined if no allocations are provided or if the total weight is zero.
 */
export const weightedAverageRating = <T extends Record<number, string>>(ratings: T, allocations?: AllocationDetails[]) => {
  let total = 0;
  let weighted = 0;

  if (!allocations || allocations.length === 0) {
    return;
  }

  allocations.forEach(allocation => {
    const ratingValue = ratings[allocation.name as keyof T] as number;
    if (ratingValue !== undefined) {
      total += allocation.weight;
      weighted += ratingValue * allocation.weight;
    }
  });

  if (total === 0) {
    return;
  }

  const ratingValue = Math.round(weighted / total);

  return ratings[ratingValue];
};

/**
 * Calculate weighted average of fund values for property path
 * @param {PortfolioHolding[]} holdings Portfolio holdings to be calculated
 * @param {DataItem[]} data Data for holdings or equity items
 * @param {string} path the path of the property to calculate
 * @return {number} weighted average value
 */
export const weightedAverageForHoldings = (holdings: PortfolioHolding[], data: DataItem[], path: string): number | undefined => {
  if (holdings.length === 0) {
    return undefined;
  }

  let numerator = 0;
  let denominator = 0;

  holdings.forEach(holding => {
    const item = data.find(d => d._id === holding.original.id);
    if (!item) {
      throw new Error('Holding not found');
    }

    const weight = holding.weight / 100;

    numerator += get(item, path, 0) * weight;
    denominator += weight;
  });

  if (denominator === 0) {
    return undefined;
  }

  return numerator / denominator;
};

/**
 * Calculate harmonic weighted average of fund values for property path
 * @param {Portfolio} portfolio Portfolio to be calculated
 * @param {Holding[]} data Data for holdings
 * @param {string} path the path of the property to calculate
 * @return {number} weighted average value
 */
export const harmonicWeightedAverage = (portfolio: Portfolio, data: InvestmentItem[], path: string): number | undefined => {
  const { holdings } = portfolio;

  if (holdings.length === 0) {
    return undefined;
  }

  let denominator = 0;
  let numerator = 0;

  holdings.forEach(holding => {
    const item = data.find(d => d._id === holding.original.id);
    if (!item) {
      throw new Error('Holding not found');
    }

    const value = get(item, path);
    const weight = holding.weight / 100;

    if (weight > 0 && value !== 0 && value != null) {
      numerator += weight;
      denominator += weight / value;
    }
  }, 0);

  if (numerator === 0 || denominator === 0) {
    return undefined;
  }

  return numerator / denominator;
};

/**
 * Calculates the periodical rate value from a return (in percentage)
 * @param {number} rate the return value for the specified period (in percentage)
 * @param {number} numberOfPeriods the number of periods to get the distribute the rate over
 */
export const calculatePeriodicalRate = (rate: number, numberOfPeriods: number) => {
  const periodicalRate = (rate / 100 + 1) ** (1 / numberOfPeriods) - 1;
  return periodicalRate * 100;
};

/**
 * Get the effective return over a period of {numberOfDays} days from the daily return.
 * @param dailyReturn The daily return (in percentage)
 * @param numberOfDays Number of days to apply the return for
 */
export const compoundDailyGrowthOverNDays = (dailyReturn: number, numberOfDays: number) => {
  const value = (dailyReturn / 100 + 1) ** numberOfDays - 1;
  return value * 100;
};

export const calculateGrowthWithFee = (growth: number, annualFee: number, numberOfDays: number, annualizationYears: Nullable<number>) => {
  /**
   * Vague TODO - I'm sure this function can be simplified with the help of an algebra expert (or simply with more time).
   * Since we're limited on time (originated from the context of a hotfix), we'll leave it as is for now since the results
   * are still accurate.
   */

  if (!annualFee) {
    return growth;
  }

  // If period is greater than 1Y, the growth is already annualized - otherwise use the number of days in period.
  const annualizationPeriod = annualizationYears ? 365 : numberOfDays;

  // Calculate daily fee from annual fee
  const dailyFee = calculatePeriodicalRate(annualFee, 365);

  // Calculate daily growth from given trailing return
  const dailyGrowth = calculatePeriodicalRate(growth, annualizationPeriod);

  // Calculate adjusted daily growth
  const adjustedGrowth = dailyGrowth - dailyFee;

  // Compound adjusted growth over the total period
  return compoundDailyGrowthOverNDays(adjustedGrowth, annualizationPeriod);
};

const getDaysForPeriod = (period: string, date: Date) => {
  if (period === TIME_PERIODS.YT) {
    return moment(date).dayOfYear();
  }

  if (period === TIME_PERIODS['1D']) {
    return 1;
  }

  if (period === TIME_PERIODS.MT) {
    return moment.utc(date).days();
  }

  if (period === TIME_PERIODS['6M']) {
    return moment(date).diff(moment(date).subtract(6, 'months'), 'days');
  }

  if (period === TIME_PERIODS['3M']) {
    return moment(date).diff(moment(date).subtract(3, 'months'), 'days');
  }

  if (period === TIME_PERIODS['1M']) {
    return moment(date).diff(moment(date).subtract(1, 'months'), 'days');
  }

  if (period === TIME_PERIODS['1W']) {
    return 7;
  }

  const annualPeriods = [TIME_PERIODS['1Y'], TIME_PERIODS['2Y'], TIME_PERIODS['3Y'], TIME_PERIODS['4Y'], TIME_PERIODS['5Y'], TIME_PERIODS['10Y']];
  if (annualPeriods.indexOf(period as TIME_PERIODS) === -1) {
    return 0;
  }

  const years = periodToYear(period as TimePeriods);

  return moment(date).diff(moment(date).subtract(years, 'years'), 'days');
};

/**
 * Calculate the portfolio's Percent Growths as weighed average of the underlying holdings
 * @param {Portfolio} portfolio the portfolio to calculate Percent Growths for
 * @param {Holding[]} data the underlying holdings of a portfolio
 * @param {number} taxRate the current tax rate
 * @param {boolean} shortHistory if the performance should be calculated with short history
 * @returns {PercentGrowth} the Percent Growths of a portfolio
 */
export const calculatePercentGrowth = (portfolio: Portfolio, data: Holding[], taxRate: number, shortHistory?: boolean): PercentGrowth => {
  const percentGrowthOverride = portfolio.override?.percentGrowth != null ? (portfolio.override.percentGrowth as PercentGrowth) : null;
  if (percentGrowthOverride !== null) {
    return { ...percentGrowthOverride };
  }

  const hasClosedHoldings = data.some(h => !!h.inactive);
  if (hasClosedHoldings) {
    return { ...DEFAULT_GROWTH_VALUES };
  }

  let growthDate: Nullable<Date> = null;

  const growthDates = data.map(datum => datum?.dates?.growth).filter(date => date !== undefined || date !== null) as Date[];
  if (growthDates.length === 0) {
    return { ...DEFAULT_GROWTH_VALUES };
  }

  growthDate = growthDates.reduce((prev, curr) => (prev > curr ? prev : curr));

  const discretionaryFees = portfolio.discretionaryMer || 0;
  const annualFee: number = discretionaryFees + discretionaryFees * taxRate;

  const percentGrowthPeriods = shortHistory
    ? [...Object.keys(TIME_PERIODS)].filter(p => p !== TIME_PERIODS.INC && p !== TIME_PERIODS.CI)
    : getCommonPeriods(portfolio, data, 'percentGrowth');

  const percentGrowth = percentGrowthPeriods.reduce(
    (acc: PercentGrowth, period: string) => {
      const growthValue = weightedAverage(portfolio, data, `performance.percentGrowth.${period}`);

      if (growthValue == null || growthDate == null) {
        return {
          ...acc,
          [period]: null,
        };
      }

      const daysInPeriod: Nullable<number> = getDaysForPeriod(period, growthDate);
      if (daysInPeriod === null) {
        return {
          ...acc,
          [period]: null,
        };
      }

      return {
        ...acc,
        [period]: calculateGrowthWithFee(growthValue, annualFee, daysInPeriod, PERIOD_YEARS[period]),
      };
    },
    { ...DEFAULT_GROWTH_VALUES },
  );

  return percentGrowth;
};

/**
 * Calculate the portfolio's calendar year returns as a weighed average of the underlying holdings
 * @param {Portfolio} portfolio the portfolio to calculate calendar year returns for
 * @param {Holding[]} data the underlying holdings of a portfolio
 * @param {boolean} shortHistory if the performance should be calculated with short history
 * @returns {CalendarYearReturns} the calendar year returns of a portfolio
 */
export const calculateCalendarYearReturns = (portfolio: Portfolio, data: Holding[], taxRate: number, shortHistory?: boolean): CalendarYearReturns => {
  const years = shortHistory ? getLastNYears(MAX_CALENDAR_RETURNS) : getCommonYears(portfolio, data);
  return years.reduce((acc: CalendarYearReturns, year) => {
    const growth = weightedAverage(portfolio, data, `performance.calendarYear.${year}`);

    if (growth === undefined) {
      acc[year] = growth;
      return acc;
    }

    const discretionaryFees = portfolio.discretionaryMer || 0;
    const annualFee = discretionaryFees + discretionaryFees * taxRate;

    return {
      ...acc,
      [year]: calculateGrowthWithFee(growth, annualFee, 365, 1),
    };
  }, {});
};

/**
 * Trim the rolling performance data for a specific time frame
 * @param {[number, number][]} period the rolling performance for a period (weekly or monthly)
 * @param {number} startTimestamp the timestamp in ms to filter after
 * @param {number} endTimestamp the timestamp in ms to filter before
 * @param {boolean} includeBoundaries true if startTimestamp and endTimestamp are included, false otherwise
 * @returns {[number,number][]} a new trimmed array
 */
export const trimRollingData = (
  period: [number, number][] | undefined,
  startTimestamp: number,
  endTimestamp: number,
  includeBoundaries = true,
): [number, number][] => {
  if (!period || period.length === 0) {
    return [];
  }
  let startIdx = 0;
  let endIdx = period.length - 1;
  while (startIdx <= period.length - 1) {
    if ((includeBoundaries && period[startIdx][0] >= startTimestamp) || (!includeBoundaries && period[startIdx][0] > startTimestamp)) {
      break;
    }
    startIdx++;
  }
  while (endIdx >= 0) {
    if ((includeBoundaries && period[endIdx][0] <= endTimestamp) || (!includeBoundaries && period[endIdx][0] < endTimestamp)) {
      break;
    }
    endIdx--;
  }
  return period.slice(startIdx, endIdx + 1);
};

// Helper function to get mean of items
const mean = (set: number[], isSample: boolean = false) => set.reduce(sum, 0) / (set.length - (isSample ? 1 : 0));

type VarianceMetrics = {
  averageReturn: number | null;
  variance: number | null;
  standardDeviation: number | null;
  varianceData: number[];
  squaredVarianceData: number[];
};

type ValidPeriod = {
  period: RiskPeriods;
  sliceCount: number;
  valid: boolean;
  numOfYears: number;
};

export type DrawdownData = {
  /**
   * Drawdowns for the rolling period
   */
  drawdowns: [Timestamp, number][];
  /**
   * Maximum drawdown value
   */
  maxDrawdown: number | null;
  /**
   * Timestamp at which the drawdown recovered to the previous peak
   */
  recoveryTimestamp?: number;
  /**
   * Timestamp of the minimum valley during drawdown
   */
  valleyTimestamp?: number;
  /**
   * Timestamp of the peak value
   */
  peakTimestamp?: number;
  /**
   * number of periods it took before reaching maximum drawdown
   */
  drawdownPeriodCount?: number;
  /**
   * number of periods it took to recover after reaching maximum drawdown
   */
  recoveryPeriodCount?: number;
};

/**
 * Returns the annualized return and the compound gross returns
 * @param {number[]} rolling the rolling data
 * @param {number} numOfYears the num of years to use for the annualization factor
 */
export const calculateCompoundReturns = (rolling: number[], numOfYears: number) => {
  if (numOfYears <= 0) {
    throw new Error('Number of years cannot be zero');
  }
  const grossReturns = rolling.map(r => 1 + r / 100);
  const annualizationFactor = 1 / numOfYears;
  const annualizedReturn = grossReturns.reduce(product, 1) ** annualizationFactor - 1;
  return { annualizedReturn, grossReturns };
};

/**
 * Format an array of rolling data
 * @param rolling the rolling performance data
 * @param minDataPoints the minimum number of data points to slice from
 * @param beginTimestamp the staring timestamp to filter after
 * @param endTimestamp the ending timestamp to filter before
 * @returns {number[]} the trimmed rolling data without the timestamps
 */
export const formatRollings = (rolling: [number, number][], minDataPoints: number, beginTimestamp: number, endTimestamp: number): number[] =>
  trimRollingData(rolling, beginTimestamp, endTimestamp)
    .slice(-minDataPoints)
    .map(p => p[1]);

/**
 * Get a variance metrics object from a set of numbers
 * Average Return, Variance and Standard Deviation
 * @param {number[]} dataPoints Set of numbers to compare
 * @param {boolean} isSample Flag to specify dataPoints are a subset of data
 * @returns {VarianceMetrics} a `VarianceMetrics` Object
 */
export const getVarianceMetrics = (dataPoints: number[], isSample: boolean = true): VarianceMetrics =>
  Calculator.getInstance().risk().varianceMetrics(dataPoints, isSample);

/**
 * Get covariance between two sets of numbers
 * @param {number[]} baseVariance Base set of of variance data to compare
 * @param {number[]} compareVariance Comparison set of variance data
 * @param {boolean} isSample Flag to indicate if data is sample or not
 */
export const getCovariance = (baseVariance: number[], compareVariance: number[], isSample: boolean = true) => {
  if (baseVariance.length !== compareVariance.length || (baseVariance.length === 0 && compareVariance.length === 0)) {
    return null;
  }
  const total = baseVariance.reduce((acc, v, i) => v * compareVariance[i] + acc, 0);
  return total / (baseVariance.length - (isSample || !baseVariance.length ? 1 : 0));
};

/**
 * Calculate Maximum Drawdown for a rolling return dataset, can be any period
 * @param {number[]} rolling Rolling performance for calculated period
 */
export const calculateMaximumDrawdown = (rolling: number[]) => Calculator.getInstance().risk().maximumDrawdown(rolling);

/**
 * Calculate the drawdowns for a rolling performance array (can be any period - weekly or monthly)
 * @param {[number, number][]} rolling Timestamped Rolling performance for period
 * @return {DrawdownData} Drawdown data for the rolling data
 */
export const calculateDrawdownData = (rolling: [number, number][]): DrawdownData => Calculator.getInstance().risk().drawdown(rolling);

/**
 * Gets the valid periods to calculate risk metrics for based on the difference in months
 * between the starting timestamp and the ending timestamp, YT is considered valid if the last time stamp lines up with
 * the end of the previous month. All timestamps assume a UTC timezone.
 * @param {number} firstTimeStamp the first timestamp in the data
 * @param {number} lastTimeStamp the last timestamp in the data
 * @param {minDataPointsMonthly} fullDataLength the length of the data points that the period is being evaluated for, if the
 * value is provided then the INC will be added as a valid period (if there at least more than 1 data point)
 * @param {number} commonInceptionLength length common inception value to set the CI period
 * @returns {ValidPeriod[]} an array of valid periods
 */
export const getValidPeriods = (firstTimeStamp: number, lastTimeStamp: number, fullDataLength?: number, commonInceptionStart?: number): ValidPeriod[] =>
  Calculator.getInstance().risk().validPeriods(firstTimeStamp, lastTimeStamp, fullDataLength, commonInceptionStart);

/**
 * Get the timestamps of the rolling period
 * @param {[number,number][]} rolling an array of rollings
 * @param {number} minDataPoints the minimum data points to slice from
 */
export const getTimestamps = (rolling: [number, number][], minDataPoints: number) => rolling.slice(-minDataPoints).map(p => p[0]);

/**
 * Validates that the timestamps of the rollings are the same length and values
 * @param {[number,number][]} holdingRollings holding rolling data
 * @param {[number,number][]} benchmarkRollings benchmark or riskFree rolling data
 * @param {number} minDataPoints the minimum data points to slice from
 */
export const validateRollings = (holdingRollings: [number, number][], benchmarkRollings: [number, number][], minDataPoints: number) => {
  const holding = getTimestamps(holdingRollings, minDataPoints);
  const benchmark = getTimestamps(benchmarkRollings, minDataPoints);
  return JSON.stringify(holding) === JSON.stringify(benchmark);
};

/**
 * Calculate risk metrics based on a set of rolling data (Useful for flexible benchmarking)
 * @param {[number, number][]} item the rolling performance of the item
 * @param {[number, number][]} benchmark the market benchmark rolling performance
 * @param {[number, number][]} riskFree the risk free benchmark rolling performance
 * @returns {Risk} the risk metrics of the item as compared to its benchmarks
 */
export const calculateRiskMetrics = (
  item: [number, number][],
  benchmark: [number, number][] = [],
  riskFree: [number, number][] = [],
  ignoreINC: boolean = false,
  commonInception?: CommonInception,
): Risk => Calculator.getInstance().risk().all(item, benchmark, riskFree, ignoreINC, commonInception).risk;

export const calculateMarketRiskMetrics = (rawRolling: number[], rawBenchmark: number[], numOfYears: number, riskFree?: number[]) =>
  Calculator.getInstance().risk().marketRiskMetrics(rawRolling, rawBenchmark, numOfYears, riskFree);

export const calculateBestWorstPerformance = (monthlyRolling: [Timestamp, number][]) => {
  const intervals: Dictionary<string[]> = {
    [TIME_PERIODS['1Y']]: [TIME_PERIODS['3M'], TIME_PERIODS['6M']],
    [TIME_PERIODS['3Y']]: [TIME_PERIODS['3M'], TIME_PERIODS['6M'], TIME_PERIODS['1Y']],
    [TIME_PERIODS['5Y']]: [TIME_PERIODS['3M'], TIME_PERIODS['6M'], TIME_PERIODS['1Y']],
    [TIME_PERIODS['10Y']]: [TIME_PERIODS['3M'], TIME_PERIODS['6M'], TIME_PERIODS['1Y'], TIME_PERIODS['5Y']],
  };
  return Object.keys(intervals).reduce((acc, currKey) => {
    const values = intervals[currKey];
    for (const value of values) {
      const periodMonths = Number(periodToMonths(currKey as TimePeriods) || 0);
      const periodData = monthlyRolling.slice(-periodMonths);
      const windowSize = Number(periodToMonths(value as TimePeriods) || 0);
      if (periodData.length < periodMonths) {
        values.forEach(v => {
          acc[currKey] = {
            ...acc[currKey],
            [v]: {
              best: { value: null, start: null, end: null },
              worst: { value: null, start: null, end: null },
            },
          };
        });
        return acc;
      }
      const rolling = periodData.slice(0, windowSize);
      const { annualizedReturn } = calculateCompoundReturns(
        rolling.map(r => r[1]),
        1,
      );
      const initialVal = {
        value: annualizedReturn * 100,
        start: new Date(rolling[0][0]),
        end: new Date(rolling[rolling.length - 1][0]),
      };
      let best: BestWorst = { ...initialVal };
      let worst: BestWorst = { ...initialVal };
      for (let i = windowSize; i < periodData.length; i++) {
        rolling.shift();
        rolling.push(periodData[i]);
        const numOfYears = rolling.length >= 12 ? rolling.length / 12 : 1;
        const { annualizedReturn: windowReturn } = calculateCompoundReturns(
          rolling.map(r => r[1]),
          numOfYears,
        );
        const returnVal = windowReturn * 100;
        const startDate = new Date(rolling[0][0]);
        startDate.setUTCMonth(startDate.getUTCMonth() - 1);
        const dates = { start: startDate, end: new Date(rolling[rolling.length - 1][0]) };
        if (returnVal < (worst.value || 0)) {
          worst = { value: returnVal, ...dates };
        }
        if (returnVal > (best.value || 0)) {
          best = { value: returnVal, ...dates };
        }
      }
      acc[currKey] = { ...acc[currKey], [value]: { best, worst } };
    }
    return acc;
  }, {} as BestWorstPerformance);
};

/**
 * Calculates the number of months an investment was up or down in the following
 * periods (1y,3y,5y,10y,INC,YT)
 * @param monthlyRolling the monthly rolling performance
 * @returns {UpDownMonths} the number of up and down months per period
 */
export const calculateUpDownMonths = (monthlyRolling: number[]): UpDownMonths => {
  const defaultValue = { up: 0, down: 0 };
  const defaultUpDown: UpDownMonths = {
    [TIME_PERIODS['1Y']]: defaultValue,
    [TIME_PERIODS['3Y']]: defaultValue,
    [TIME_PERIODS['5Y']]: defaultValue,
    [TIME_PERIODS['10Y']]: defaultValue,
    [TIME_PERIODS.YT]: defaultValue,
    [TIME_PERIODS.INC]: defaultValue,
    [TIME_PERIODS.CI]: defaultValue,
  };
  return Object.keys(defaultUpDown).reduce(
    (acc, currKey) => {
      const periodMonths = Number(periodToMonths(currKey as TimePeriods) || 0);
      const periodData = monthlyRolling.slice(-periodMonths);
      if (periodData.length < periodMonths) {
        acc[currKey as RiskPeriods] = defaultValue;
        return acc;
      }
      let up = 0;
      let down = 0;
      let i = 0;
      while (i < periodData.length) {
        if (periodData[i] > 0) {
          up++;
        } else if (periodData[i] < 0) {
          down++;
        }
        i++;
      }
      acc[currKey as RiskPeriods] = { up, down };
      return acc;
    },
    { ...defaultUpDown },
  );
};

/**
 * Gets the Percentage growth between 2 prices (annualized for more than 1 year)
 * @param startPrice The price at the start of the period
 * @param endPrice The price at the end of the period
 * @param years the number of years in the period (for annualization)
 * @returns {number} the percentage growth
 */
export const calculateGrowth = (startPrice: number, endPrice: number, years?: number): number => {
  const totalGrowth = (endPrice - startPrice) / startPrice;
  if (years != null && years > 0) {
    return ((1 + totalGrowth) ** (1 / years) - 1) * 100;
  }
  return totalGrowth * 100;
};

/**
 * Calculates the percentage growths for a price dataset (daily prices)
 * @param {Array<PriceData>} data the data to calculate
 */
export const calculatePercentGrowthFromPrices = (data: PriceData[], includeINC = false) => {
  const percentGrowth: PercentGrowth = {
    ...DEFAULT_GROWTH_VALUES,
  };
  if (!data || !data.length) {
    return percentGrowth;
  }
  percentGrowth.YT = 0; // Default value for YT is 0.
  const first = data[0];
  const firstDate = moment.utc(first.date).startOf('day');
  const currentYear = moment.utc(first.date).startOf('day').year();
  if (data.length >= 2) {
    percentGrowth['1D'] = calculateGrowth(data[1].price, first.price);
  }
  if (includeINC && data.length >= 2) {
    const lastDataPoint = data[data.length - 1];
    const yearDiff = firstDate.diff(moment.utc(lastDataPoint.date).startOf('day'), 'years', true);
    percentGrowth.INC = calculateGrowth(lastDataPoint.price, first.price, yearDiff >= 1 ? yearDiff : 0);
  }

  for (const item of data) {
    const yearDiff = firstDate.diff(moment.utc(item.date).startOf('day'), 'years');
    const monthDiff = firstDate.diff(moment.utc(item.date).startOf('day'), 'months');
    const weekDiff = firstDate.diff(moment.utc(item.date).startOf('day'), 'weeks');
    /*
     Check if the month changed - since the data is sorted from latest to oldest
     if the month changes, we know we are in the last available date from the prev month
     */
    const monthChange = firstDate.month() !== moment.utc(item.date).startOf('day').month();
    if (monthChange && percentGrowth.MT == null) {
      percentGrowth.MT = calculateGrowth(item.price, first.price);
    }
    if (yearDiff === 0 && monthDiff === 0 && weekDiff === 1 && percentGrowth['1W'] == null) {
      percentGrowth['1W'] = calculateGrowth(item.price, first.price);
    }
    if (yearDiff >= 10 && percentGrowth['10Y'] == null) {
      percentGrowth['10Y'] = calculateGrowth(item.price, first.price, 10);
    }
    if (yearDiff >= 5 && percentGrowth['5Y'] == null) {
      percentGrowth['5Y'] = calculateGrowth(item.price, first.price, 5);
    }
    if (yearDiff >= 4 && percentGrowth['4Y'] == null) {
      percentGrowth['4Y'] = calculateGrowth(item.price, first.price, 4);
    }
    if (yearDiff >= 3 && percentGrowth['3Y'] == null) {
      percentGrowth['3Y'] = calculateGrowth(item.price, first.price, 3);
    }
    if (yearDiff >= 2 && percentGrowth['2Y'] == null) {
      percentGrowth['2Y'] = calculateGrowth(item.price, first.price, 2);
    }
    if (yearDiff >= 1 && percentGrowth['1Y'] == null) {
      percentGrowth['1Y'] = calculateGrowth(item.price, first.price);
    }
    if (yearDiff === 0 && monthDiff === 6 && percentGrowth['6M'] == null) {
      percentGrowth['6M'] = calculateGrowth(item.price, first.price);
    }
    if (yearDiff === 0 && monthDiff === 1 && percentGrowth['1M'] == null) {
      percentGrowth['1M'] = calculateGrowth(item.price, first.price);
    }
    if (yearDiff === 0 && monthDiff === 3 && percentGrowth['3M'] == null) {
      percentGrowth['3M'] = calculateGrowth(item.price, first.price);
    }
    if (currentYear !== moment.utc(item.date).startOf('day').year() && !percentGrowth.YT) {
      percentGrowth.YT = calculateGrowth(item.price, first.price);
    }
  }
  return percentGrowth;
};

/**
 * Calculates the calendar year returns for a price dataset (daily prices)
 * @param {Array<PriceData>} data the data to calculate
 */
export const calculateYearlyReturns = (data: PriceData[]) => {
  const yearReturns: CalendarYearReturns = {};
  if (!data || !data.length) {
    return yearReturns;
  }
  const currentYear = new Date().getUTCFullYear();
  const grouped = data.reduce(
    (acc, curr) => {
      const key = new Date(curr.date).getUTCFullYear();
      (acc[key] || (acc[key] = [])).push(curr.price);
      return acc;
    },
    {} as Dictionary<number[]>,
  );
  if (grouped[currentYear]) {
    delete grouped[currentYear];
  }
  for (const [year, value] of Object.entries(grouped)) {
    if (value.length > 2) {
      const first = value[value.length - 1];
      const last = value[0];
      yearReturns[year.toString()] = calculateGrowth(first, last);
    }
  }
  return yearReturns;
};

export const calculateGrowthFromRolling = (trimmedRolling: [Timestamp, number][], years?: number) =>
  annualize(calculateTotalReturns(trimmedRolling), years) * 100;

export const annualize = (totalGrowth: number, years?: number) => {
  if (years) {
    return (1 + totalGrowth) ** (1 / years) - 1;
  }

  return totalGrowth;
};

export const calculateTotalReturns = (trimmedRolling: [Timestamp, number][]) => {
  const end = trimmedRolling.reduce((acc, [, val]) => acc + acc * (val / 100), 1);

  return end - 1;
};

export const filterRollingByYear = (rolling: [Timestamp, number][], year: number) => rolling.filter(([date]) => new Date(date).getFullYear() === year);

/**
 * Calculate calendar year returns from a monthly rolling series
 * @param {[Timestamp, number][]} rolling rolling data
 */
export const calculateCalendarYearFromRolling = (rolling: [Timestamp, number][]) =>
  getLastNYears(10).reduce((acc: CalendarYearReturns, year) => {
    const trimmedRolling = filterRollingByYear(rolling, year);

    if (trimmedRolling.length < 12) {
      return acc;
    }

    acc[year] = calculateGrowthFromRolling(trimmedRolling);
    return acc;
  }, {});

/**
 * Calculates the weekly rollings for a a price dataset (daily prices)
 * @param {Array<PriceData>} data the data to calculate
 */
export const calculateWeeklyRollings = (data: PriceData[]) => {
  if (!data || !data.length) {
    return [];
  }
  let lastWeek = moment(data[0].date).utc().week();
  let endOfWeekPrice = data[0];
  const lastTimeStamp = moment(data[0].date).utc().valueOf();

  return data
    .reduce(
      (acc, curr, idx) => {
        const currentWeek = moment(curr.date).utc().week();
        if (currentWeek !== lastWeek || idx + 1 === data.length) {
          const timestamp = moment.utc(endOfWeekPrice.date).weekday(5).startOf('day').valueOf();
          const weeklyReturn = calculateGrowth(curr.price, endOfWeekPrice.price);
          endOfWeekPrice = curr; // keep track of last time the week changed
          lastWeek = currentWeek;
          if (timestamp <= lastTimeStamp) {
            acc.push([timestamp, weeklyReturn]);
          }
        }
        return acc;
      },
      [] as [Timestamp, number][],
    )
    .sort((a, b) => a[0] - b[0]);
};

/**
 * Calculates the monthly rollings a price dataset (daily prices)
 * @param {Array<PriceData>} data the data to calculate
 */
export const calculateMonthlyRollings = (data: PriceData[]) => {
  if (!data || !data.length) {
    return [];
  }
  let lastMonth = moment(data[0].date).utc().month();
  let endOfMonthPrice = data[0];
  const lastTimeStamp = getLastDayOfPreviousMonth().getTime();
  return data
    .reduce(
      (acc, curr, idx) => {
        const currentMonth = moment(curr.date).utc().month();
        if (currentMonth !== lastMonth || idx + 1 === data.length) {
          let { price } = curr;
          if (
            idx + 1 === data.length &&
            moment(curr.date).utc().month() ===
              moment(data[idx - 1].date)
                .utc()
                .month()
          ) {
            price = curr.price;
          }
          const timestamp = moment.utc(endOfMonthPrice.date).endOf('month').startOf('day').valueOf();
          const monthlyReturn = calculateGrowth(price, endOfMonthPrice.price);
          if (timestamp <= lastTimeStamp) {
            acc.push([timestamp, monthlyReturn]);
          }
          endOfMonthPrice = curr;
          lastMonth = currentMonth; // keep track of last time the month changed
        }
        return acc;
      },
      [] as [Timestamp, number][],
    )
    .sort((a, b) => a[0] - b[0]);
};

export const monthlyToWeeklyReturns = (returns: [Timestamp, number][]) => {
  const weeklyReturns = returns.reduce(
    (acc, [d, value]) => {
      const date = new Date(d);
      date.setUTCHours(0, 0, 0, 0);
      const fridays = [];
      const friday = moment(date).utc().startOf('month').day('Friday').startOf('day');
      if (friday.date() > 7) {
        friday.add(7, 'd');
      }
      const month = friday.month();
      while (month === friday.month()) {
        fridays.push(friday.toDate());
        friday.add(7, 'd');
      }
      const periodicalRate = (1 + value / 100) ** (1 / (52 / 12)) - 1;
      for (const friday of fridays) {
        acc.push([friday.getTime(), periodicalRate * 100]);
      }
      return acc;
    },
    [] as [Timestamp, number][],
  );

  return weeklyReturns;
};

/**
 * Calculates the weekly NAVs from a price dataset (daily prices)
 * @param {Array<PriceData>} data the data to calculate
 */
export const calculateWeeklyNAV = (data: PriceData[]) => {
  if (!data || !data.length) {
    return [];
  }
  let lastWeek = moment(data[0].date).utc().week();
  let endOfWeekPrice = data[0];
  const lastDate = new Date();
  lastDate.setHours(0, 0, 0, 0);
  lastDate.setUTCHours(0, 0, 0, 0);
  const lastTimeStamp = lastDate.getTime();

  return data
    .reduce(
      (acc, curr, idx) => {
        const currentWeek = moment(curr.date).utc().week();
        /*  idx + 1 === data.length captures the NAV for the earliest week in the prices data.
        Ex. If the earliest price is Jan 28, 2010, we want to account for the Jan 31, 2010 price in the NAVs,
        even though the week never flipped.
    */
        if (currentWeek !== lastWeek || idx + 1 === data.length) {
          const timestamp = moment.utc(endOfWeekPrice.date).weekday(5).startOf('day').valueOf();
          if (timestamp <= lastTimeStamp) {
            acc.push([timestamp, endOfWeekPrice.price]);
          }
          endOfWeekPrice = curr;
          lastWeek = currentWeek; // keep track of last time the month changed
        }
        return acc;
      },
      [] as [Timestamp, number][],
    )
    .sort((a, b) => a[0] - b[0]);
};

/**
 * Calculates the monthly NAVs for a price dataset (daily prices)
 * @param {PriceData[]} data the data to calculate
 */
export const calculateMonthlyNAV = (data: PriceData[]) => {
  if (!data || !data.length) {
    return [];
  }
  let lastMonth = moment(data[0].date).utc().month();
  let endOfMonthPrice = data[0];
  const lastTimeStamp = getLastDayOfPreviousMonth().getTime();
  return data
    .reduce(
      (acc, curr, idx) => {
        const currentMonth = moment(curr.date).utc().month();

        /*  idx + 1 === data.length captures the NAV for the earliest month in the prices data.
        Ex. If the earliest price is Jan 15, 2010, we want to account for the Jan 31, 2010 price in the NAVs,
        even though the month never flipped.
    */
        if (currentMonth !== lastMonth || idx + 1 === data.length) {
          const timestamp = moment.utc(endOfMonthPrice.date).endOf('month').startOf('day').valueOf();
          if (timestamp <= lastTimeStamp) {
            acc.push([timestamp, endOfMonthPrice.price]);
          }
          endOfMonthPrice = curr;
          lastMonth = currentMonth; // keep track of last time the month changed
        }
        return acc;
      },
      [] as [Timestamp, number][],
    )
    .sort((a, b) => a[0] - b[0]);
};

/**
 * Normalizes timestamped data to contain all end of month values
 * Ex. If we have daily prices for a security, this function will return an array of data
 *     containing all the end of month prices (Jan 31, Feb 28, Mar 31, etc)
 * @param {[Timestamp, number][]} data timestamped data normalized to end of month
 */
export const normalizeDataToMonthly = (data: [Timestamp, number][]) => {
  if (!data || !data.length) {
    return [];
  }
  const sortedData = data.slice().sort((a, b) => a[0] - b[0]);
  const last = moment(sortedData[0][0]).utc();
  let lastMonth = last.month();
  let lastYear = last.year();
  let endOfMonthValue = sortedData[0];

  let total = 0;
  return sortedData.reduce(
    (acc, curr, idx) => {
      const current = moment(curr[0]).utc();
      const currentMonth = current.month();
      const currentYear = current.year();

      // if the month or the year changed, save the value and prepare the next bucket.
      if (currentMonth !== lastMonth || currentYear !== lastYear) {
        const timestamp = moment.utc(endOfMonthValue[0]).endOf('month').startOf('day').valueOf();

        acc.push([timestamp, total]);
        total = 0;
        endOfMonthValue = curr;
        lastMonth = currentMonth; // keep track of last time the month changed
        lastYear = currentYear;
      }

      total += curr[1];

      // if last item, save to bucket.
      if (idx + 1 === sortedData.length) {
        const timestamp = moment.utc(endOfMonthValue[0]).endOf('month').startOf('day').valueOf();
        acc.push([timestamp, total]);
      }

      return acc;
    },
    [] as [Timestamp, number][],
  );
};

/**
 * Gets the latest weekly price for a holding, if pricing data is available.
 * @param type Type of the holding
 * @param holdingData Data for the holding
 * @returns Array containing the timestamp of the price, and the price itself.
 */
export const getLatestPriceForHolding = (type: PortfolioHoldingType, holdingData: Holding): [Timestamp, number] | undefined => {
  let nav: Array<[Timestamp, number]> | undefined;

  switch (type) {
    case HOLDING_TYPES.FUND: {
      const { latestNav } = (holdingData as Fund).nav;
      if (latestNav) {
        return latestNav;
      }
      nav = (holdingData as Fund).nav.weekly;
      break;
    }
    case HOLDING_TYPES.STOCK: {
      const { latestNav } = (holdingData as Stock).nav;
      if (latestNav) {
        return latestNav;
      }
      nav = (holdingData as Stock).nav.weekly;
      break;
    }
    case HOLDING_TYPES.ALT:
    case HOLDING_TYPES.CUSTOM_YIELD:
    case HOLDING_TYPES.BENCHMARK:
    default: {
      nav = undefined;
    }
  }

  if (!nav || nav.length === 0) {
    return undefined;
  }

  return nav[nav.length - 1];
};

/**
 * Evaluate the number of units from a dollars amount for a specific holding, based on the latest pricing data available.
 * @param type Type of the holding
 * @param dollars Dollars amount
 * @param holdingData Data for the holding
 */
export const getUnitsFromDollarsAmount = (type: PortfolioHoldingType, dollars: number, holdingData: Holding): number | undefined => {
  const priceData = getLatestPriceForHolding(type, holdingData);

  // If price is 0, units mathematically cannot be calculated.
  if (priceData == null || priceData[1] === 0) {
    return undefined;
  }

  return round(dollars / priceData[1], 4);
};

/**
 * Evaluate the dollars amount corresponding to a set number of units for a specific holding, based on the latest pricing data available.
 * @param type Type of the holding
 * @param units Number of units
 * @param holdingData Data for the holding
 */
export const getDollarsAmountFromUnits = (type: PortfolioHoldingType, units: number, holdingData: Holding): number | undefined => {
  const priceData = getLatestPriceForHolding(type, holdingData);

  if (priceData == null) {
    return undefined;
  }

  return round(priceData[1] * units, 2);
};

export const addPortfolioHoldingsAllocations = (portfolio: Portfolio, portfolioData?: PortfolioData): PortfolioHolding[] => {
  if (!portfolioData) {
    return portfolio.holdings;
  }

  switch (portfolio.allocationMode) {
    case HoldingAllocationMode.Units: {
      return portfolio.holdings.map(holding => {
        const data = getPortfolioHoldingData(holding, portfolioData);

        // Process lots if they exist
        if (holding.lots && holding.lots.length > 0 && data) {
          const updatedLots = holding.lots.map(lot => {
            // Calculate dollars from units for each lot if missing
            if (lot.units != null && lot.dollars == null) {
              const dollars = getDollarsAmountFromUnits(holding.original.type, lot.units, data) || 0;
              return {
                ...lot,
                dollars,
              };
            }
            return lot;
          });

          // Recalculate holding's total units and dollars from lots
          const totalUnits = updatedLots.reduce((sum, lot) => sum + (lot.units || 0), 0);
          const totalDollars = updatedLots.reduce((sum, lot) => sum + (lot.dollars || 0), 0);

          return {
            ...holding,
            lots: updatedLots,
            units: totalUnits,
            dollars: totalDollars,
            weight: portfolio.size ? round((totalDollars / portfolio.size) * 100, 10) : 0,
          };
        }

        // If portfolios has units but is missing dollars, calculate those.
        if (holding.units != null && holding.dollars == null) {
          if (!data) {
            return holding;
          }

          const dollars = getDollarsAmountFromUnits(holding.original.type, holding.units, data) || 0;

          return {
            ...holding,
            dollars,
            weight: portfolio.size ? round((holding.dollars / portfolio.size) * 100, 10) : 0,
          };
        }

        return holding;
      });
    }
    case HoldingAllocationMode.Weight:
    default: {
      // No need to do anything - return the current holdings.
      return portfolio.holdings;
    }
  }
};

/**
 * Calculates correlation for each pair of an array of items
 * @param {CorrelationDataInput[]} data an array of correlation data inputs
 * @param {number} sliceCount the number of elements to slice from the end of the array, used to select a specific time period within the data
 * @returns {CorrelationData[]} the correlation data between each pair of items
 */
export const calculateCorrelationMatrix = (data: CorrelationDataInput[], sliceCount: number): CorrelationData[] => {
  const correlationData: CorrelationData[] = [];

  const varianceMetricsItems = data.map(item => {
    const hasEnoughData = item.rolling != null && item.rolling.length >= sliceCount;
    if (hasEnoughData) {
      const startDate = item.rolling[0][0];
      const endDate = item.rolling[item.rolling.length - 1][0];
      const rawData = formatRollings(item.rolling, sliceCount, startDate, endDate);
      return { _id: item._id, varianceMetrics: getVarianceMetrics(rawData) };
    }
    return null;
  });

  for (let i = 0; i < varianceMetricsItems.length; i++) {
    const base = varianceMetricsItems[i];
    if (base) {
      for (let j = i + 1; j < varianceMetricsItems.length; j++) {
        const compare = varianceMetricsItems[j];
        if (compare) {
          const { correlation } = Calculator.getInstance().risk().correlationMetrics(base.varianceMetrics, compare.varianceMetrics);
          correlationData.push({
            holdings: [base._id, compare._id],
            value: correlation,
          });
        }
      }
    }
  }

  return correlationData;
};

export function calculateTrailingReturns(rolling: [Timestamp, number][]): PercentGrowth {
  const periods = [
    ['YT', null],
    ['1M', 1],
    ['3M', 3],
    ['6M', 6],
    ['1Y', 12],
    ['2Y', 24],
    ['3Y', 36],
    ['4Y', 48],
    ['5Y', 60],
    ['10Y', 120],
    ['INC', null],
  ] as [GrowthTypes, number | null][];

  return periods.reduce((acc, [key, months]) => {
    if (key === 'INC') {
      // Calculating return since inception
      const totalYears = rolling.length / 12;
      acc[key] = calculateGrowthFromRolling(rolling, totalYears);
      return acc;
    }

    if (key === 'YT') {
      if (rolling && rolling.length > 1) {
        const endOfLastYear = moment(rolling[rolling.length - 1][0])
          .utc()
          .subtract(1, 'year')
          .endOf('year')
          .valueOf();
        acc[key] = calculateGrowthFromRolling(rolling.filter(r => r[0] > endOfLastYear));
      } else {
        acc[key] = 0;
      }
      return acc;
    }

    // If rolling doesn't have enough rolling data to cover period, skip.
    if (months == null || rolling.length < months) {
      return acc;
    }

    // Calculating a trailing return with a defined period (ie: 6M, 1Y, 3Y, ...)
    // If period is greater or equal to 1Y, annualize the return.
    const years = months >= 12 ? months / 12 : undefined;
    acc[key] = calculateGrowthFromRolling(rolling.slice(-months), years);

    return acc;
  }, {} as PercentGrowth);
}

/**
 * Gets the common monthly rolling from the start recent item
 * @param {[Timestamp, number][]} rolling monthly performance
 * @param {Timestamp} startDate from inception period
 * @returns {[Timestamp, number][]} the common monthly rolling since inception
 */
export const getCommonMonthlyRolling = (rolling: [Timestamp, number][] = [], startDate: Timestamp = 0): [Timestamp, number][] => {
  if (!rolling.length || startDate === 0) {
    return [];
  }

  const commonMonthly: [Timestamp, number][] = [];
  for (let index = rolling.length - 1; index >= 0; index--) {
    if (startDate > rolling[index][0]) break;
    commonMonthly.push(rolling[index]);
  }

  return commonMonthly.length ? commonMonthly.reverse() : [];
};

/**
 * Calculates the comparison item growth from the start of the item itself
 */
export const calculatePortfolioSinceInception = (
  itemRollingMonthly: [Timestamp, number][],
  holdings: ComparisonHolding[],
  useLongerHistory?: boolean,
): Nullable<number> => {
  if (!itemRollingMonthly) {
    return null;
  }

  const time = getMaxCommonInceptionDate([{ holdings, type: COMPARISON_ITEM_TYPES.PORTFOLIO }], useLongerHistory);
  if (!time) {
    return null;
  }

  const commonMonthly = getCommonMonthlyRolling(itemRollingMonthly, time);

  if (commonMonthly.length) {
    const years = commonMonthly?.length / 12;
    return calculateGrowthFromRolling(commonMonthly, years);
  }

  return null;
};

/**
 * Calculates the item growth from the start of the recent item in the comparison
 */
export const calculatePerformanceCommonInception = (itemRollingMonthly: [Timestamp, number][], startTimestamp?: Timestamp): Nullable<number> => {
  if (startTimestamp == null || itemRollingMonthly == null) {
    return null;
  }

  // TODO CI
  // if (isEqual(item._id, recentItem._id) && itemPerformance.percentGrowth?.INC) {
  //  return itemPerformance.percentGrowth.INC;
  // }

  const commonMonthly = getCommonMonthlyRolling(itemRollingMonthly, startTimestamp);

  if (commonMonthly.length) {
    const years = commonMonthly?.length / 12;
    return calculateGrowthFromRolling(commonMonthly, years);
  }

  return null;
};

/**
 * Calculates the sum of values from a specific path across portfolio holdings.
 * Returns null if no valid values are found.
 *
 * @template T - Type extending object with _id property
 * @param {Portfolio} portfolio - The portfolio containing holdings to sum
 * @param {T[]} items - Array of items (funds, stocks, alts etc.) containing the values to sum
 * @param {string} path - Lodash-style path to the value to sum (e.g. 'commitment.size')
 * @param {HOLDING_TYPES} [holdingType] - Optional holding type to filter holdings (e.g. 'alt', 'fund')
 * @returns {number | null} The sum of all values found at the specified path, or null if no values found
 *
 * @example
 * // Sum all alt commitment sizes
 * const totalCommitment = sumHoldingValues(portfolio, alts, 'commitment.size', HOLDING_TYPES.ALT);
 *
 * @example
 * // Sum distributions across all holdings
 * const totalDistributed = sumHoldingValues(portfolio, allItems, 'distributed');
 */
export const sumHoldingValues = <T extends { _id: string }>(portfolio: Portfolio, items: T[], path: string, holdingType?: string): number | null => {
  const filteredHoldings = holdingType ? portfolio.holdings.filter(h => h.original.type === holdingType) : portfolio.holdings;

  const values = filteredHoldings
    .map(holding => {
      const item = items.find(i => i._id === holding.original.id);
      return item ? get(item, path, null) : null;
    })
    .filter(value => value !== null) as number[];

  if (values.length === 0) return null;

  return values.reduce(sum, 0);
};
